import React, { useEffect, useRef, useState } from 'react';
import { Icon, Typography } from '@design-system';
import './DropdownButton.scss';

export type DropdownButtonAction = {
    label: string;
    onClick: () => void;
    icon?: React.ReactNode;
};

export type DropdownButtonProps = {
    icon: React.ReactNode;
    actions: DropdownButtonAction[];
    color?: 'marine' | 'green' | 'grey' | 'red' | 'yellow' | 'blue';
    size?: number;
    variant?: 'plain' | 'surrounded';
    position?: 'left' | 'right';
} & React.HTMLAttributes<HTMLDivElement>;

export const DropdownButton: React.FC<DropdownButtonProps> = ({
    icon,
    actions,
    color = 'grey',
    size = 28,
    variant = 'plain',
    position = 'right',
    className,
    ...props
}) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
            setIsOpen(false);
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <div className={`dropdown-button ${className ?? ''}`} ref={dropdownRef} {...props}>
            <div onClick={() => setIsOpen(!isOpen)} className="dropdown-button-icon">
                <Icon icon={icon} size={size} color={color} variant={variant} />
            </div>

            {isOpen && actions.length > 0 && (
                <div className="dropdown-content" data-position={position}>
                    {actions.map((action, index) => (
                        <div
                            key={index}
                            className="dropdown-item"
                            onClick={() => {
                                action.onClick();
                                setIsOpen(false);
                            }}
                        >
                            {action.icon && <Icon icon={action.icon} size={20} color={color} />}
                            <Typography intrinsic="p" color="grey">
                                {action.label}
                            </Typography>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
